import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';
import { MembersCountCell } from '../components/RemoveMember';
import { formatDateUS } from '../lib/format';

const price = (c) => ((c || 0) > 0 ? <span className="pill pro">${(c / 100).toFixed(2)}</span> : <span className="pill free">Free</span>);

/** Draft (not saved yet) · Past (date before today) · Upcoming. */
function eventStatus(e) {
  if (e.draft) return ['Draft', '#f3f4f6', 'var(--muted)'];
  const today = new Date().toISOString().slice(0, 10);
  if (e.date && String(e.date).slice(0, 10) < today) return ['Past', '#f3f4f6', 'var(--muted)'];
  return ['Upcoming', 'var(--green-bg)', 'var(--green-text)'];
}

// Events the signed-in user created. Open one to edit/manage it.
export default function LeaderEventsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [creating, setCreating] = useState(false);

  // Create a draft immediately and open its editor — mirrors CoursesAdminPage.
  // Lets the publisher use AssociationTabs (which needs a real UUID) right
  // away, instead of the two-step "fill form → save → then associate" flow.
  const newEvent = async () => {
    setCreating(true);
    try {
      const created = await api.createEvent({
        requestName: 'Untitled event',
        state: 'STARTED',
        priceCents: 0,
        isPrivate: false,
        draft: true, // hidden from others until the first Save
      });
      navigate(`/leader/event/${created.id}?created=1`);
    } catch (e) { alert(e.message); setCreating(false); }
  };

  const { loading, error, data, reload } = useAsync(async () => {
    const all = await api.listEvents();
    const list = Array.isArray(all) ? all : [];
    // 2026-10-17 — superadmins manage every event (the server already allows it).
    if (me?.isAdmin) return list;
    return list.filter((p) => p.ownerUsername && me?.username && p.ownerUsername === me.username);
  }, [me?.username, me?.isAdmin]);
  const counts = useAsync(() => api.memberCounts('event').catch(() => ({})), []);

  return (
    <div className="page">
      <PageHead
        icon="📅"
        title="Manage events"
        sub="Open an event to edit its details, date, price and conversation. Members and invites are under Members."
        actions={<button className="btn btn-primary" disabled={creating} onClick={newEvent}>{creating ? 'Creating…' : '+ New event'}</button>}
      />
      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <Section style={{ padding: 0 }}>
          {data.length === 0 ? <div style={{ padding: 18 }}><Empty>You haven't created any events yet.</Empty></div> : (
            <table className="tbl">
              <thead><tr><th>Event</th><th>Date &amp; time</th><th>Price</th><th>Members</th><th>Status</th></tr></thead>
              <tbody>
                {data.map((e) => {
                  const [label, bg, fg] = eventStatus(e);
                  return (
                    <tr key={e.id} className="clickable" style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/leader/event/${e.id}`)}>
                      <td>
                        <b>{e.requestName || '(unnamed event)'}</b>
                        {(e.topic || e.groupName) && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                            {[e.topic, e.groupName ? `in ${e.groupName}` : null].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {me?.isAdmin && e.ownerUsername && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                            by @{e.ownerUsername}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {e.date ? formatDateUS(e.date) : <span style={{ color: 'var(--muted)' }}>—</span>}
                        {e.beginTime ? ` · ${e.beginTime}` : ''}
                      </td>
                      <td>{price(e.priceCents)}</td>
                      <td><MembersCountCell count={counts.loading ? undefined : (counts.data?.[e.id] ?? 0)} tab="events" id={e.id} /></td>
                      <td><span className="pill" style={{ background: bg, color: fg }}>{label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </div>
  );
}
